import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import { Checkbox, FormControlLabel } from '@mui/material';

import CheckBlack from '@images/svg-icons/checkBlack.svg';
import CheckBlackEmpty from '@images/svg-icons/checkBlackEmpty.svg';

import WrapperGoogleMap from '@components/styled-UI/WrapperGoogleMap';

import { IPhysicalStore, SetDefaultStorePayload } from '@interfaces/myProfileInterfaces';

import { removeFavouriteStore, setDefaultStore } from '@services/myProfileServices';

import { getLabelFallback } from '@utils/utils';
import {
    getFullAddress,
    MyStoresPlaceHoldersQueryParamsConfig,
    WEEKDAYS,
} from '@utils/pages-utils/MyProfileUtils';

import { Marker } from '@react-google-maps/api';
import locationPinMap from 'src\assets\images\svg-icons\locationPinMap.svg';

interface Props {
    physicalStore: IPhysicalStore;
}

const PhysicalStore = ({ physicalStore }: Props): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);

    const dispatch = useDispatch();

    /** Get the phyisical store hours formatted with the week days */
    const getStoreHours = (storeHours: string): JSX.Element[] => {
        const hours: JSX.Element[] = [];
        storeHours.split(';').map((storeHour, index) => {
            hours.push(
                <div className={`${WEEKDAYS[index]}`} key={index}>
                    <span className="secondary-message small bold align-left">
                        {getLabelFallback(
                            `MY_STORES_PHYSICAL_STORE_${WEEKDAYS[index].toUpperCase()}_LABEL`
                        )}
                    </span>
                    <span className="secondary-message small">{storeHour}</span>
                </div>
            );
        });
        return hours;
    };

    const redirectToScheduleEyeExam = (physicalStoreId: string) => {
        const queryParamsConfig: MyStoresPlaceHoldersQueryParamsConfig = {
            storeNumber: physicalStoreId,
            catalogId: config.catalogId,
            langId: config.langId,
            storeId: config.storeId,
        };
        let externalUrl: string = config.callFromComponentToTO.myProfile.myStores.scheduleEyeExam;
        window.open(
            externalUrl
                .replace('{physicalStoreId}', queryParamsConfig.storeNumber)
                .replace('{catalogId}', queryParamsConfig.catalogId)
                .replace('{langId}', queryParamsConfig.langId)
                .replace('{storeId}', queryParamsConfig.storeId),
            '_parent'
        );
    };

    const redirectToBookingDisplayView = (physicalStoreId: string) => {
        let externalUrl: string = config.callFromComponentToTO.myProfile.myStores.otherServices;
        window.open(externalUrl.replace('{storeNumber}', physicalStoreId), '_parent');
    };

    /* Capitalize string words excluding state  */

    const capitalizeWords = (str) => {
        return str
            .toLowerCase()
            .split(' ')
            .map((word) => word.length === 3 && word.slice(-1) === ',' ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    /* Render the upper part of the phyisical store component */

    const renderUpperPhysicalStore = (
        storeId: string,
        preferredStoreNumber: string,
        storeName: string
    ): JSX.Element => {
        const store = storeName.split(', ');
        const [isDefault, setIsDefault] = useState<boolean>(preferredStoreNumber === '1');

        /** TODO: Handle set default and get favourites stores services */
        const handleSetDefaultStore = (event: React.ChangeEvent<HTMLInputElement>) => {
            setIsDefault(event.target.checked);
            const payload: SetDefaultStorePayload = {
                storeId: config.storeId,
                catalogId: config.catalogId,
                preferredStoreNumber: +preferredStoreNumber,
                redirectView: 'PreferredStoresView',
            };
            dispatch(setDefaultStore(payload.storeId, payload));
        };

        return (
            <div className="physical-store_upper">
                <FormControlLabel
                    classes={{
                        label: 'default-store_label primary-message medium bold',
                        root: `default-store ${isDefault ? 'default' : ''}`,
                    }}
                    control={
                        <Checkbox
                            size="small"
                            icon={<CheckBlackEmpty />}
                            checkedIcon={<CheckBlack />}
                            checked={isDefault}
                            onChange={handleSetDefaultStore}
                        />
                    }
                    label={getLabelFallback(
                        `${isDefault
                            ? 'MY_STORES_PHYSICAL_STORE_TITLE'
                            : 'MY_STORES_SET_AS_DEFAULT_STORE'
                        }`
                    )}
                />
                <button
                    className="physical-store_upper-button primary-button-negative black button-size-small"
                    data-element-id="MyStores_DefaultStore"
                    data-description="Remove"
                    onClick={() => dispatch(removeFavouriteStore(config.storeId, storeId))}
                >
                    {getLabelFallback('MY_STORES_PHYSICAL_STORE_REMOVE_CTA')}
                </button>
                <div className="physical-store_upper-title">
                    <div className='physical-store_upper-title-left'><b>{capitalizeWords(store[0])}</b>,</div><div className='physical-store_upper-title-right'>{capitalizeWords(store[1])}</div>
                </div>
            </div>
        );
    };

    /* Render the body part of the physical store component */
    const renderBodyPhysicalStore = (physicalStore: IPhysicalStore): JSX.Element => {
        return (
            <div className="physical-store_body">
                <WrapperGoogleMap
                    googleMapsApiKey={config?.googleMapsKey}
                    mapContainerClassName="physical-store-map"
                    center={{
                        lat: +physicalStore.latitude,
                        lng: +physicalStore.longitude,
                    }}
                    options={{
                        zoomControl: false,
                        fullscreenControl: false,
                        disableDoubleClickZoom: false,
                        draggable: false,
                        disableDefaultUI: true,
                    }}
                >
                    <Marker
                        position={{
                            lat: +physicalStore.latitude,
                            lng: +physicalStore.longitude,
                        }}
                        icon={{
                            url: 'https://assets.targetoptical.com/extra/image/TargetOptical/global/to-location-icon.png',
                        }}
                        clickable={false}
                    />
                </WrapperGoogleMap>
                <div className="physical-store-details_address">
                    <span className="secondary-message medium bold align-left address-label-mb8">
                        {getLabelFallback('MY_STORES_PHYSICAL_STORE_ADDRESS_LABEL')}
                    </span>
                    <div className="primary-message medium align-left mb-10 full-address">
                        {capitalizeWords(getFullAddress(physicalStore))}
                    </div>
                    <div className="contact-info">
                        <a className="link-button" href={`tel:${physicalStore.telephone1}`}>
                            {physicalStore.telephone1}
                        </a>
                        <a
                            className="link-button"
                            href={`http://maps.google.com/?q=${getFullAddress(physicalStore)}`}
                            target="_blank"
                        >
                            {getLabelFallback('MY_STORES_PHYSICAL_STORE_DIRECTION_LABEL')}
                        </a>
                    </div>
                </div>
                <div className="physical-store-details_hours">
                    <span className="secondary-message medium bold align-left">
                        {getLabelFallback('MY_STORES_PHYSICAL_STORE_HOURS_LABEL')}
                    </span>
                    <div className="store-hours-container">
                        {getStoreHours(physicalStore.storeHours)}
                    </div>
                </div>
                <div className="buttons-wrapper">
                    <button
                        data-element-id=""
                        data-description="Schedule"
                        className="primary-button button-size-small"
                        onClick={() => redirectToScheduleEyeExam(physicalStore.storeIdentifier)}
                    >
                        {getLabelFallback('MY_STORES_PHYSICAL_STORE_SCHEDULE_EYE_EXAM_CTA')}
                    </button>
                </div>
            </div>
        );
    };
    return (
        <div className="physical-store content-container">
            {renderUpperPhysicalStore(
                physicalStore.uniqueID,
                physicalStore.x_preferredStoreNumber,
                physicalStore.storeName
            )}
            {renderBodyPhysicalStore(physicalStore)}
        </div>
    );
};
export default PhysicalStore;
